import { type RefObject, useEffect, useRef } from 'react';
import { Assert } from '@team/assert';

type OnOnceElementAppearHookOptions = {
  elementRef: RefObject<HTMLElement>;
  onAppear: VoidFunction;
  options?: IntersectionObserverInit;
  shouldTrigger?: boolean;
};

export function useOnOnceElementAppear({
  elementRef,
  onAppear,
  options,
  shouldTrigger = true,
}: OnOnceElementAppearHookOptions) {
  const hasAppeared = useRef(false);
  const { threshold, root, rootMargin } = options ?? {};
  const handle = useRef(onAppear);

  handle.current = onAppear;

  useEffect(() => {
    if (hasAppeared.current) {
      return;
    }

    if (!shouldTrigger) {
      return;
    }

    if (Assert.isNull(elementRef.current)) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || hasAppeared.current) {
          return;
        }

        handle.current();

        hasAppeared.current = true;
        observer.disconnect();
      },
      {
        threshold,
        root,
        rootMargin,
      }
    );

    observer.observe(elementRef.current);

    return () => {
      observer.disconnect();
    };
  }, [elementRef, threshold, root, rootMargin, shouldTrigger]);
}
